import { useRef } from 'react'
import type { KeyboardEvent } from 'react'
import { SimpleGrid, Stack, Text } from '@mantine/core'
import { exactNumber } from '../../utils/format'
import {
  VERDICT_ORDER, cellDescription, dayLabel, offsetLabel, sameInstant, timelineRows, verdictLabel,
  verdictMeaning,
} from './scheduleFormat'
import type { ScheduleLoadBucket, ScheduleLoadVerdict } from '../../types/scheduleLoad'

/**
 * The key to the timeline, with the window's tally per verdict.
 *
 * Every verdict is listed even at a count of zero, with its meaning in words
 * beside it — on screen, not in a tooltip — because the difference between
 * Indeterminate and Clear is the single most important thing on this page to
 * read correctly, and a colour key alone cannot carry it. The swatches reuse the
 * grid's own cell class so the key and the grid cannot drift apart.
 */
export function TimelineLegend({ counts }: { counts: Record<string, number> }) {
  return (
    <Stack gap="xs">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="xs" verticalSpacing={6}>
        {VERDICT_ORDER.map((verdict) => (
          <LegendEntry key={verdict} verdict={verdict} count={counts[verdict] ?? 0} />
        ))}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span
            className="lh-tide-cell lh-tide-swatch"
            data-verdict="Indeterminate"
            data-floor="Contended"
            aria-hidden="true"
          />
          <Text size="xs">
            <strong>Indeterminate, over a tint</strong> — the known demand alone already contends; the unknowns
            can only add to it.
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span className="lh-tide-cell lh-tide-swatch" data-starts="true" aria-hidden="true" />
          <Text size="xs">
            <strong>Dot</strong> — something is scheduled to start in this quarter-hour. How many, and what, is
            in the table below.
          </Text>
        </div>
      </SimpleGrid>
    </Stack>
  )
}

function LegendEntry({ verdict, count }: { verdict: ScheduleLoadVerdict; count: number }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <span className="lh-tide-cell lh-tide-swatch" data-verdict={verdict} aria-hidden="true" />
      <Text size="xs">
        <strong>{verdictLabel(verdict)}</strong>{' '}
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>({exactNumber(count)})</span>
        {' — '}
        {verdictMeaning(verdict)}
      </Text>
    </div>
  )
}

const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21]

/**
 * Every quarter-hour in the window, on a wall-clock grid in the display zone.
 *
 * Columns are wall-clock positions (96 a day), so 15:00 is the same column on
 * every row and a daily spike lines up down the page. Rows are (date, offset):
 * see `timelineRows` for why a fall-back night is two rows.
 *
 * **The grid is the overview, not the evidence.** Each cell's colour is its
 * verdict and a dot says something starts in it; the counts, costs and unknowns
 * behind the colour are in the quarter-hour table and the detail panel, never
 * only in a tooltip (S3). The native `title` is the hover layer, and it carries
 * exactly the words the cell's accessible name does.
 *
 * Keyboard: one tab stop for the whole grid (the selected cell), arrows move
 * through quarter-hours and selection follows focus — a week is 672 cells, and
 * 672 tab stops would make everything below the grid unreachable in practice.
 */
export function LoadTimeline({
  buckets, selectedUtc, onSelect,
}: {
  buckets: ScheduleLoadBucket[]
  selectedUtc: string | null
  onSelect: (startUtc: string) => void
}) {
  const container = useRef<HTMLDivElement>(null)
  const rows = timelineRows(buckets)
  const selectedIndex = Math.max(0, buckets.findIndex((b) => sameInstant(b.startUtc, selectedUtc)))

  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = event.key === 'ArrowRight' ? 1
      : event.key === 'ArrowLeft' ? -1
        : event.key === 'ArrowDown' ? 96
          : event.key === 'ArrowUp' ? -96
            : event.key === 'Home' ? -index
              : event.key === 'End' ? buckets.length - 1 - index
                : 0
    if (step === 0) return
    event.preventDefault()
    // A day up or down (96 quarter-hours — the same UTC instant a day away, which
    // is the same wall clock except across an offset change) does nothing when
    // there is no such day, rather than jumping to the window's edge.
    const vertical = event.key === 'ArrowUp' || event.key === 'ArrowDown'
    if (vertical && (index + step < 0 || index + step > buckets.length - 1)) return
    const next = Math.min(buckets.length - 1, Math.max(0, index + step))
    onSelect(buckets[next].startUtc)
    container.current?.querySelector<HTMLButtonElement>(`[data-index="${next}"]`)?.focus()
  }

  return (
    <div className="lh-tide-scroll">
      <div className="lh-tide-grid" ref={container} role="group" aria-label="Quarter-hours in the window">
        <span style={{ gridRow: 1, gridColumn: 1 }} />
        {HOUR_TICKS.map((hour) => (
          <span
            key={hour}
            className="lh-tide-hour"
            style={{ gridRow: 1, gridColumn: `${2 + hour * 4} / span 12` }}
          >
            {String(hour).padStart(2, '0')}:00
          </span>
        ))}

        {rows.map((row, r) => (
          <RowCells
            key={row.key}
            gridRow={r + 2}
            date={row.date}
            offsetMinutes={row.offsetMinutes}
            cells={row.cells}
            selectedIndex={selectedIndex}
            onSelect={onSelect}
            onKey={move}
          />
        ))}
      </div>
    </div>
  )
}

function RowCells({
  gridRow, date, offsetMinutes, cells, selectedIndex, onSelect, onKey,
}: {
  gridRow: number
  date: string | null
  offsetMinutes: number | null
  cells: { column: number; bucket: ScheduleLoadBucket; index: number }[]
  selectedIndex: number
  onSelect: (startUtc: string) => void
  onKey: (event: KeyboardEvent<HTMLButtonElement>, index: number) => void
}) {
  return (
    <>
      <span className="lh-tide-rowlabel" style={{ gridRow, gridColumn: 1 }}>
        {date ? dayLabel(date) : 'Unreadable time'}
        {/* On every row, not only on change days: a row's offset is what makes its
            wall clock mean one instant, and it costs one line. */}
        <small>{offsetLabel(offsetMinutes)}</small>
      </span>
      {cells.map(({ column, bucket, index }) => {
        const description = cellDescription(bucket)
        const selected = index === selectedIndex
        return (
          <button
            key={bucket.startUtc}
            type="button"
            className="lh-tide-cell"
            style={{ gridRow, gridColumn: column + 2 }}
            data-index={index}
            data-verdict={bucket.verdict}
            data-floor={bucket.floorVerdict ?? undefined}
            data-starts={bucket.starts > 0 ? 'true' : undefined}
            data-hour-start={column % 4 === 0 && column > 0 ? 'true' : undefined}
            aria-pressed={selected}
            aria-label={description}
            title={description}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(bucket.startUtc)}
            onKeyDown={(event) => onKey(event, index)}
          />
        )
      })}
    </>
  )
}
